import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  flatMap,
  map,
  switchMap
} from 'rxjs/operators';

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ModalSetting } from '@shared/components/modal/modal-setting';
import {
  DialogButtonColour,
  DialogComponent
} from '@shared/components/dialog/dialog.component';
import {
  ContextMenuItem,
  DisplayColour
} from '@shared/components/menu/context-menu-item';
import { DatesUtil } from '@shared/utilities/dates/dates.util';

import { TaskStatus, taskStatusOptions } from '@shared/models/tasks';
import { TaskEditComponent } from '../edit/task-edit.component';
import { TaskNewComponent } from '../new/task-new.component';
import { GuidUtil } from '@liberty-financial/ng-core';
import { TaskService } from '@features/tasks/services/task.service';
import { TaskConfig, TASK_CONFIG } from '@features/tasks/services/task-config';
import { ConsolidatedTask, Task, TaskType } from '@features/tasks/models/task';
import { TaskContextMenuComponent } from '../../pipes/task-context-menu.pipe';
import { TaskNavigationUrlPipe } from '../../pipes/task-navigation-url.pipe';
import { TagEntityType } from '@features/opportunities/services/tag.service';
import { Tag } from '@shared/models/tag';

type TaskWithTags = Task.Model & { tags: Tag[] };

@Component({
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss']
})
export class TaskListComponent implements OnInit, OnDestroy, TaskContextMenuComponent {
  public search$: BehaviorSubject<[string]> = new BehaviorSubject<[string]>([
    ''
  ]);

  entityId$ = this.taskService.entityId$;
  entityId!: string;
  tagEntityType: TagEntityType = TagEntityType.Opportunities;
  modal: BsModalRef | null = null;
  subscriptions: Subscription[] = [];
  selectedTaskStatus: TaskStatus[] = [TaskStatus.InProgress];

  public load$: Observable<TaskWithTags[]> = this.entityId$.pipe(
    flatMap((entityId) => {
      this.entityId = entityId;
      return this.search$.pipe(
        switchMap(([searchString]) =>
          this.taskService.getTasks(this.entityId, { searchString }).pipe(
            map((tasks) =>
              tasks.filter((x) => x.status == this.selectedTaskStatus[0])
            ),
            map(this.sortTasks),
            switchMap((tasks) => {
              if (tasks.length === 0) return of([] as TaskWithTags[]);
              return forkJoin(
                tasks.map(task =>
                  this.taskService.getTaskTags(this.entityId, task.id).pipe(
                    map(tags => ({ ...task, tags } as TaskWithTags))
                  )
                )
              );
            })
          )
        )
      );
    })
  );

  taskStatusOptions = taskStatusOptions;

  searchControl: UntypedFormControl = new UntypedFormControl();
  public form: UntypedFormGroup = this.formBuilder.group({
    searchControl: this.searchControl
  });

  constructor(
    protected modalService: BsModalService,
    protected formBuilder: UntypedFormBuilder,
    protected taskService: TaskService,
    private navigationUrlPipe: TaskNavigationUrlPipe,
    @Inject(TASK_CONFIG) private readonly config: TaskConfig
  ) {}

  ngOnInit(): void {
    this.subscribeToChanges();
    this.subscriptions.push(
      this.taskService.tagEntityType$.subscribe(t => this.tagEntityType = t)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private subscribeToChanges(): void {
    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((_: string) => {
          this.refreshList();
        })
    );
  }

  protected sortTasks(tasks: Task.Model[]): Task.Model[] {
    return tasks.sort((a, b) => {
      //NOTE: this fails at runtime if referencing a method declared on this class.
      let daysA = DatesUtil.differenceInDays(
        DatesUtil.convertToDate(a.due) ?? new Date()
      );
      let daysB = DatesUtil.differenceInDays(
        DatesUtil.convertToDate(b.due) ?? new Date()
      );
      return daysB - daysA;

      //TODO: Should really sort withing priority for tasks due on the same day.
    });
  }

  protected refreshList(): void {
    this.search$.next([this.searchControl.value]);
  }

  protected closeModal(): void {
    this.modal?.hide();
  }

  protected closeAndRefresh(): void {
    this.closeModal();
    this.refreshList();
  }

  public confirmCompleteTask(taskId: string): void {
    const initialState = {
      title: 'Complete Task',
      message: 'This action cannot be undone.',
      buttons: [
        {
          label: 'Cancel',
          action: of([]),
          complete: () => this.closeModal(),
          colour: DialogButtonColour.default
        },
        {
          label: 'Complete',
          action: this.completeTask(taskId),
          complete: () => this.closeAndRefresh(),
          colour: DialogButtonColour.action
        }
      ]
    };
    this.modal = this.modalService.show(
      DialogComponent,
      ModalSetting.createDialog(initialState)
    );
  }

  public confirmDeleteTask(taskId: string): void {
    const initialState = {
      title: 'Delete Task',
      message: 'This action cannot be undone.',
      buttons: [
        {
          label: 'Cancel',
          action: of([]),
          complete: () => this.closeModal(),
          colour: DialogButtonColour.default
        },
        {
          label: 'Delete',
          action: this.deleteTask(taskId),
          complete: () => this.closeAndRefresh(),
          colour: DialogButtonColour.danger
        }
      ]
    };
    this.modal = this.modalService.show(
      DialogComponent,
      ModalSetting.createDialog(initialState)
    );
  }

  public onChoiceChange(event: string[]): void {
    this.selectedTaskStatus = event.map((x) => x as TaskStatus);
    this.refreshList();
  }

  addTask(): void {
    this.closeModal();
    this.modal = this.modalService.show(
      TaskNewComponent,
      ModalSetting.createLargeModal(
        {
          entityId: this.entityId,
          taskId: GuidUtil.generate(),
          cancel: () => this.closeModal(),
          saved: () => this.closeAndRefresh()
        },
        'details-modal'
      )
    );
  }

  editTask(taskId: string): void {
    this.closeModal();
    this.modal = this.modalService.show(
      TaskEditComponent,
      ModalSetting.createLargeModal(
        {
          entityId: this.entityId,
          taskId: taskId,
          cancel: () => this.closeModal(),
          saved: () => this.closeAndRefresh()
        },
        'details-modal'
      )
    );
  }

  completeTask(taskId: string): Observable<any> {
    return this.taskService.completeTask(this.entityId, taskId);
  }

  deleteTask(taskId: string): Observable<any> {
    return this.taskService.deleteTask(this.entityId, taskId);
  }

  titleNavigationUrl(task: TaskWithTags): string[] {
    if (!this.isReviewTask(task.taskType)) {
      return [];
    }
    return this.navigationUrlPipe.transform({
      entityId: this.entityId,
      entityType: ConsolidatedTask.EntityType.Opportunity,
      taskType: task.taskType
    });
  }

  private isReviewTask(taskType: TaskType | undefined): boolean {
    return (
      taskType === TaskType.FactFindAttested ||
      taskType === TaskType.FactFindDocumentsPublished
    );
  }
}
